import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
  
import api from '../services/api';

// Create the context
const DashboardContext = createContext();

export const useDashboard = () => useContext(DashboardContext);

// Simple API execution - no retries, no complex logic
const executeApiCall = async (apiCall) => {
  return await apiCall();
};

export const DashboardProvider = ({ children }) => {
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  

  
  // Add refs to prevent duplicate calls (like LeadContext) - per endpoint
  const lastCallsRef = useRef(new Map()); // endpoint -> callKey
  const currentRequestIdRef = useRef(null);
  const isLoadingPerEndpointRef = useRef(new Map()); // endpoint -> boolean
  
  // Cleanup effect (like LeadContext)
  useEffect(() => {
    return () => {
      lastCallsRef.current.clear();
      currentRequestIdRef.current = null;
      isLoadingPerEndpointRef.current.clear();
    };
  }, []);

  // Simple error handler - just set error state, no complex logic
  const handleError = (error) => {
    console.error('API Error:', error);
    setError(error);
  };

  // Simple success handler - just clear error
  const handleSuccess = () => {
    setError(null);
  };

  const fetchGeneralStats = useCallback(async (type='today', startDate, endDate, periodType) => {
    // Create unique call key (like LeadContext pattern)
    const endpoint = 'general-stats';
    const callKey = JSON.stringify({ endpoint, type, startDate, endDate, periodType });
    
    // Prevent duplicate calls for this specific endpoint
    if (lastCallsRef.current.get(endpoint) === callKey) {
      console.log('Preventing duplicate API call for general stats');
      return null;
    }
    
    // Prevent simultaneous calls for this specific endpoint
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      console.log('API call already in progress for this endpoint, skipping general stats');
      return null;
    }
    
    // Update references
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const response = await executeApiCall(() => api.post('/dashboard/general-stats/', {
        period_type: periodType || type,
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      }));
      console.log('fetchGeneralStats response.data:', response.data);
      return response.data;
    } catch (error) {
      // Check if this is still the current request
      if (currentRequestIdRef.current !== requestId) {
        console.log('Ignoring stale error for general stats');
        return null;
      }
      
      handleError(error);
      // Reset call reference on error to allow retry
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  // Cache for combined sales lead funnel data to avoid multiple API calls
  const salesLeadFunnelCacheRef = useRef({});

  const fetchSalesLeadFunnelData = async (startDate, endDate, periodType) => {
    const cacheKey = `${startDate}_${endDate}_${periodType}`;
    
    // Return cached data if available
    if (salesLeadFunnelCacheRef.current[cacheKey]) {
      return salesLeadFunnelCacheRef.current[cacheKey];
    }

    try {
      setLoading(true);
      const response = await executeApiCall(() => api.post(`/report/dashboard/sales-lead-funnel/`, {
        period_type: periodType,
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      }));
      handleSuccess();
      
      // Cache the response
      salesLeadFunnelCacheRef.current[cacheKey] = response.data;
      
      // Clear cache after 5 minutes
      setTimeout(() => {
        delete salesLeadFunnelCacheRef.current[cacheKey];
      }, 5 * 60 * 1000);
      
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesLeadFunnel = async (startDate, endDate, periodType) => {
    const data = await fetchSalesLeadFunnelData(startDate, endDate, periodType);
    return data?.funnel_steps || data || null;
  };

  const fetchSalesLeadFunnelMatrics = async (startDate, endDate, periodType) => {
    const data = await fetchSalesLeadFunnelData(startDate, endDate, periodType);
    return data?.metrics || [];
  };

  const fetchLeadConversionRatio = async (startDate, endDate, periodType) => {
    const data = await fetchSalesLeadFunnelData(startDate, endDate, periodType);
    return data?.conversion_ratios || [];
  };

  const fetchLeadSourceMetrics = async (startDate, endDate, periodType) => {    
    try {
      setLoading(true);
      // Use the new endpoint in salesgro_report
      const response = await executeApiCall(() => api.post(`/report/dashboard/lead-source-metrics/`, {
        period_type: periodType,
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      }));
      handleSuccess();
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };

  const fetchTodaysReport = async (startDate, endDate, periodType) => {
    
    try {
      setLoading(true);
      const response = await executeApiCall(() => api.post(`/dashboard/todays-report/`, {
        period_type: periodType,
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      }));
      handleSuccess();
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };

  const fetchBacklogTasks = useCallback(async () => {
    // Create unique call key (like LeadContext pattern) - backlog tasks don't support date filtering
    const endpoint = 'backlog-tasks-main';
    const callKey = JSON.stringify({ endpoint });
    
    // Prevent duplicate calls for this specific endpoint
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    // Prevent simultaneous calls for this specific endpoint
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    // Update references
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const response = await executeApiCall(() => api.get('/dashboard/backlog-tasks/'));
      return response.data;
    } catch (error) { 
      // Check if this is still the current request
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      // Reset call reference on error to allow retry
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);    
    }
  }, []);

  const fetchSiteVisits = useCallback(async (type, startDate, endDate) => {
    // Create unique call key (like LeadContext pattern)
    const endpoint = 'site-visits';
    const callKey = JSON.stringify({ endpoint, type, startDate, endDate });
    
    // Prevent duplicate calls for this specific endpoint
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    // Prevent simultaneous calls for this specific endpoint
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    // Update references
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const response = await executeApiCall(() => api.post('/dashboard/site-visits/', {
        period_type: type,
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      }));
      return response.data;
    } catch (error) { 
      // Check if this is still the current request
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      // Reset call reference on error to allow retry
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);    
    }
  }, []);

  const fetchAgentStats = async (type, startDate, endDate) => {    
    // Create unique call key (like LeadContext pattern)
    const endpoint = 'agent-stats';
    const callKey = JSON.stringify({ endpoint, type, startDate, endDate });
    
    // Prevent duplicate calls for this specific endpoint
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    // Prevent simultaneous calls for this specific endpoint
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    // Update references
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const response = await executeApiCall(() => api.post('/dashboard/agent-stats/', {
        period_type: type,
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      }));
      return response.data;
    } catch (error) {
      // Check if this is still the current request
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      // Reset call reference on error to allow retry
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  };

  // New endpoint: Lead Performance
  const fetchLeadPerformance = useCallback(async (startDate, endDate, teamFilter = [], agentFilter = []) => {
    const endpoint = 'lead-performance';
    const callKey = JSON.stringify({ endpoint, startDate, endDate, teamFilter, agentFilter });
    
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      // Add agent filter if provided
      if (agentFilter && agentFilter.length > 0) {
        requestData.agent_filter = agentFilter;
      }
      
      const response = await executeApiCall(() => api.post('/report/dashboard/lead-performance/', requestData));
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  // New endpoint: Call Performance & Discipline
  const fetchCallPerformanceDiscipline = useCallback(async (startDate, endDate, teamFilter = [], agentFilter = []) => {
    const endpoint = 'call-performance-discipline';
    const callKey = JSON.stringify({ endpoint, startDate, endDate, teamFilter, agentFilter });
    
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      // Add agent filter if provided
      if (agentFilter && agentFilter.length > 0) {
        requestData.agent_filter = agentFilter;
      }
      
      const response = await executeApiCall(() => api.post('/report/dashboard/call-performance-discipline/', requestData));
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  // New endpoint: Sales Performance
  const fetchSalesPerformance = useCallback(async (startDate, endDate, teamFilter = []) => {
    const endpoint = 'sales-performance';
    const callKey = JSON.stringify({ endpoint, startDate, endDate, teamFilter });
    
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      const response = await executeApiCall(() => api.post('/report/dashboard/sales-performance/', requestData));
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  // Lead filters (sources, etc.) - GET /report/lead/filters/
  const fetchLeadFilters = useCallback(async () => {
    try {
      const response = await executeApiCall(() => api.get('/report/lead/filters/'));
      return response?.data ?? null;
    } catch (error) {
      handleError(error);
      return null;
    }
  }, []);

  // New endpoint: Pending Actions (no date range required)
  const fetchPendingActions = useCallback(async (teamFilter = [], agentFilter = [], sourceFilter = []) => {
    const endpoint = 'pending-actions';
    const callKey = JSON.stringify({ endpoint, teamFilter, agentFilter, sourceFilter });
    
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const requestData = {};
      
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      if (agentFilter && agentFilter.length > 0) {
        requestData.agent_filter = agentFilter;
      }
      
      if (sourceFilter && sourceFilter.length > 0) {
        requestData.source_filter = sourceFilter;
      }
      
      const response = await executeApiCall(() => api.post('/report/dashboard/pending-actions/', requestData));
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  const fetchCriticalMissesToday = useCallback(async (teamFilter = [], agentFilter = [], sourceFilter = []) => {
    const endpoint = 'critical-misses-today';
    const callKey = JSON.stringify({ endpoint, teamFilter, agentFilter, sourceFilter });

    if (lastCallsRef.current.get(endpoint) === callKey) return null;
    if (isLoadingPerEndpointRef.current.get(endpoint)) return null;

    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;

    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);

      const requestData = {};
      if (teamFilter && teamFilter.length > 0) requestData.team_filter = teamFilter;
      if (agentFilter && agentFilter.length > 0) requestData.agent_filter = agentFilter;
      if (sourceFilter && sourceFilter.length > 0) requestData.source_filter = sourceFilter;

      const response = await executeApiCall(() =>
        api.post('/report/dashboard/critical-misses-today/', requestData)
      );
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) return null;
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  const fetchLeadsRecovery = useCallback(async (teamFilter = [], agentFilter = [], sourceFilter = []) => {
    const endpoint = 'leads-recovery';
    const callKey = JSON.stringify({ endpoint, teamFilter, agentFilter, sourceFilter });

    if (lastCallsRef.current.get(endpoint) === callKey) return null;
    if (isLoadingPerEndpointRef.current.get(endpoint)) return null;

    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;

    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);

      const requestData = {};
      if (teamFilter && teamFilter.length > 0) requestData.team_filter = teamFilter;
      if (agentFilter && agentFilter.length > 0) requestData.agent_filter = agentFilter;
      if (sourceFilter && sourceFilter.length > 0) requestData.source_filter = sourceFilter;

      const response = await executeApiCall(() =>
        api.post('/report/dashboard/leads-recovery/', requestData)
      );
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) return null;
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  const fetchActiveLeadsByIntent = useCallback(async (teamFilter = [], agentFilter = [], sourceFilter = []) => {
    const endpoint = 'active-leads-by-intent';
    const callKey = JSON.stringify({ endpoint, teamFilter, agentFilter, sourceFilter });

    if (lastCallsRef.current.get(endpoint) === callKey) return null;
    if (isLoadingPerEndpointRef.current.get(endpoint)) return null;

    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;

    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);

      const requestData = {};
      if (teamFilter && teamFilter.length > 0) requestData.team_filter = teamFilter;
      if (agentFilter && agentFilter.length > 0) requestData.agent_filter = agentFilter;
      if (sourceFilter && sourceFilter.length > 0) requestData.source_filter = sourceFilter;

      const response = await executeApiCall(() =>
        api.post('/report/dashboard/active-leads-by-intent/', requestData)
      );
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) return null;
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  const fetchTopPerformers = async (startDate, endDate) => {
    // Create unique call key (like LeadContext pattern)
    const endpoint = 'top-performers';
    const callKey = JSON.stringify({ endpoint, startDate, endDate });
    
    // Prevent duplicate calls for this specific endpoint
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    // Prevent simultaneous calls for this specific endpoint
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    // Update references
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      const response = await executeApiCall(() => api.get('/dashboard/top-performers/'));
      handleSuccess();
      return response.data;
    } catch (error) {
      // Check if this is still the current request
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      // Reset call reference on error to allow retry
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  };

  const fetchRecentFlaggedLeads = async () => {
    
    try {
      setLoading(true);
      const response = await executeApiCall(() => api.get('/dashboard/recent-flagged-leads/'));
      handleSuccess();
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };

  const fetchRecentLostDeals = async () => {
    
    try {
      setLoading(true);
      const response = await executeApiCall(() => api.get('/dashboard/recent-lost-deals/'));
      handleSuccess();
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setLoading(false);    
    }
  };

  // Fetch all agents' last locations (for admin dashboard)
  const fetchAgentLocations = useCallback(async () => {
    const endpoint = 'agent-locations';
    const callKey = JSON.stringify({ endpoint });
    
    if (lastCallsRef.current.get(endpoint) === callKey) {
      return null;
    }
    
    if (isLoadingPerEndpointRef.current.get(endpoint)) {
      return null;
    }
    
    lastCallsRef.current.set(endpoint, callKey);
    const requestId = Date.now() + Math.random();
    currentRequestIdRef.current = requestId;
    
    try {
      setLoading(true);
      isLoadingPerEndpointRef.current.set(endpoint, true);
      
      // PERFORMANCE: Skip geocoding for faster response
      // Coordinates are sufficient for displaying agent locations
      // Address can be fetched on-demand when viewing details
      const response = await executeApiCall(() => 
        api.post('/report/dashboard/agent-locations/', { 
          include_address: false 
        })
      );
      return response.data;
    } catch (error) {
      if (currentRequestIdRef.current !== requestId) {
        return null;
      }
      
      handleError(error);
      lastCallsRef.current.delete(endpoint);
      return null;
    } finally {
      setLoading(false);
      isLoadingPerEndpointRef.current.set(endpoint, false);
    }
  }, []);

  // Simple retry function - clear error state and reset refs (like LeadContext)
  const retryAfterServerError = () => {
    setError(null);
    lastCallsRef.current.clear(); // Allow new calls for all endpoints
    currentRequestIdRef.current = null;
    isLoadingPerEndpointRef.current.clear(); // Clear all endpoint loading states
  };

  // Function to reset duplicate call prevention - useful for tab switches
  const resetDuplicateCallPrevention = () => {
    lastCallsRef.current.clear(); // Allow new calls for all endpoints
    currentRequestIdRef.current = null;
    isLoadingPerEndpointRef.current.clear(); // Clear all endpoint loading states
    console.log('Reset duplicate call prevention - fresh API calls will be allowed');
  };

  // New endpoint: Lead Performance Trend Data
  const fetchLeadPerformanceTrend = useCallback(async (startDate, endDate, metricType = null, teamFilter = [], agentFilter = [], metricTypes = null) => {
    try {
      setLoading(true);
      
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Support both single metric_type and multiple metric_types
      if (metricTypes && Array.isArray(metricTypes) && metricTypes.length > 0) {
        requestData.metric_types = metricTypes;
      } else if (metricType) {
        requestData.metric_type = metricType;
      }
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      // Add agent filter if provided
      if (agentFilter && agentFilter.length > 0) {
        requestData.agent_filter = agentFilter;
      }
      
      const response = await executeApiCall(() => api.post('/report/dashboard/lead-performance/', requestData));
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // New endpoint: Call Performance Discipline Trend Data
  const fetchCallPerformanceTrend = useCallback(async (startDate, endDate, metricType = null, teamFilter = [], agentFilter = [], metricTypes = null) => {
    try {
      setLoading(true);
      
      const requestData = {
        date_range: {
          start_date: startDate,
          end_date: endDate
        }
      };
      
      // Support both single metric_type and multiple metric_types
      if (metricTypes && Array.isArray(metricTypes) && metricTypes.length > 0) {
        requestData.metric_types = metricTypes;
      } else if (metricType) {
        requestData.metric_type = metricType;
      }
      
      // Add team filter if provided
      if (teamFilter && teamFilter.length > 0) {
        requestData.team_filter = teamFilter;
      }
      
      // Add agent filter if provided
      if (agentFilter && agentFilter.length > 0) {
        requestData.agent_filter = agentFilter;
      }
      
      const response = await executeApiCall(() => api.post('/report/dashboard/call-performance-discipline/', requestData));
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Context value
  const value = {
    // Core operations
    fetchGeneralStats,
    fetchSalesLeadFunnel,
    fetchSalesLeadFunnelMatrics,
    fetchLeadConversionRatio,
    fetchLeadSourceMetrics,
    fetchTodaysReport,
    fetchBacklogTasks,
    fetchSiteVisits,
    fetchAgentStats,
    fetchTopPerformers,
    fetchRecentFlaggedLeads,
    fetchRecentLostDeals,
    
    // New dashboard endpoints
    fetchLeadPerformance,
    fetchCallPerformanceDiscipline,
    fetchSalesPerformance,
    fetchLeadFilters,
    fetchPendingActions,
    fetchCriticalMissesToday,
    fetchLeadsRecovery,
    fetchActiveLeadsByIntent,
    fetchAgentLocations,

    // Trend data endpoints
    fetchLeadPerformanceTrend,
    fetchCallPerformanceTrend,

    // Server status
    retryAfterServerError,
    resetDuplicateCallPrevention,
    loading,
    error,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
